import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import SignatureProgress from '../components/signature/SignatureProgress';
import { useDocuments } from '../contexts/DocumentContext';
import './DashboardPage.css';

const DashboardPage = () => {
  const { documents, loading, error: contextError, removeDocument, clearError } = useDocuments();
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingDocuments, setDeletingDocuments] = useState({});
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Apply filters when documents, statusFilter, or searchTerm change
  useEffect(() => {
    let filtered = [...documents];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(term) || 
        (doc.recipientEmail && doc.recipientEmail.toLowerCase().includes(term)) ||
        (doc.recipient_email && doc.recipient_email.toLowerCase().includes(term))
      );
    }
    
    setFilteredDocuments(filtered);
  }, [documents, statusFilter, searchTerm]);

  // Get status display text and class
  const getStatusInfo = (status) => {
    switch (status) {
      case 'draft':
        return { text: 'Draft', className: 'status-draft' };
      case 'sent':
        return { text: 'Awaiting Signature', className: 'status-sent' };
      case 'signed':
        return { text: 'Completed', className: 'status-signed' };
      default:
        return { text: status, className: `status-${status}` };
    }
  };

  // Get the appropriate link based on document status
  const getDocumentLink = (document) => {
    switch (document.status) {
      case 'draft':
        return `/editor/${document.id}`;
      case 'sent':
        return `/view/${document.id}`;
      case 'signed':
        return `/complete/${document.id}`;
      default:
        return `/editor/${document.id}`;
    }
  };

  // Format date for display in a modern way
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // If less than 24 hours ago, show relative time
    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return diffMinutes <= 1 ? 'Just now' : `${diffMinutes} minutes ago`;
      }
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // If less than 7 days ago, show day of week
    if (diffDays < 7) {
      const options = { weekday: 'short' };
      return date.toLocaleDateString(undefined, options);
    }
    
    // Otherwise show formatted date
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };
  
  // Handle document deletion
  const handleDeleteClick = (documentId) => {
    setShowDeleteConfirm(documentId);
  };
  
  const confirmDelete = async (documentId) => {
    try {
      setDeletingDocuments(prev => ({ ...prev, [documentId]: true }));
      setDeleteError(null);
      
      await removeDocument(documentId);
      
      setDeletingDocuments(prev => ({ ...prev, [documentId]: false }));
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setDeleteError(`Failed to delete document: ${err.message}`);
      setDeletingDocuments(prev => ({ ...prev, [documentId]: false }));
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading documents...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <h1 className="page-title">Document Dashboard</h1>
          <div className="dashboard-actions">
            <Link to="/upload">
              <Button>Create New Document</Button>
            </Link>
          </div>
        </div>
        
        <div className="dashboard-filters">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="status-filters">
            <button 
              className={`status-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button 
              className={`status-filter-btn ${statusFilter === 'draft' ? 'active' : ''}`}
              onClick={() => setStatusFilter('draft')}
            >
              Drafts
            </button>
            <button 
              className={`status-filter-btn ${statusFilter === 'sent' ? 'active' : ''}`}
              onClick={() => setStatusFilter('sent')}
            >
              Awaiting Signature
            </button>
            <button 
              className={`status-filter-btn ${statusFilter === 'signed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('signed')}
            >
              Completed
            </button>
          </div>
        </div>
        
        {(contextError || deleteError) && (
          <Alert 
            type="error" 
            title="Error" 
            message={contextError || deleteError} 
            className="mb-md"
            dismissible={true}
            onDismiss={() => {
              clearError();
              setDeleteError(null);
            }}
          />
        )}
        
        {showDeleteConfirm && (
          <div className="delete-confirmation-overlay">
            <Card className="delete-confirmation-dialog">
              <h3>Delete Document</h3>
              <p>Are you sure you want to delete this document? This action cannot be undone.</p>
              <div className="delete-confirmation-actions">
                <Button 
                  variant="secondary" 
                  onClick={cancelDelete}
                >
                  Cancel
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => confirmDelete(showDeleteConfirm)}
                  loading={deletingDocuments[showDeleteConfirm]}
                >
                  Delete
                </Button>
              </div>
            </Card>
          </div>
        )}
        
        {filteredDocuments.length === 0 ? (
          <div className="no-documents">
            <Card className="text-center p-lg">
              <h3>No documents found</h3>
              {statusFilter !== 'all' ? (
                <p>No documents with status "{statusFilter}" found.</p>
              ) : searchTerm ? (
                <p>No documents matching "{searchTerm}" found.</p>
              ) : (
                <p>You haven't created any documents yet.</p>
              )}
              <div className="mt-md">
                <Link to="/upload">
                  <Button>Create Your First Document</Button>
                </Link>
              </div>
            </Card>
          </div>
        ) : (
          <div className="documents-grid">
            {filteredDocuments.map(document => {
              const status = getStatusInfo(document.status);
              const documentLink = getDocumentLink(document);
              
              return (
                <Card key={document.id} className="document-card">
                  <div className="document-card-header">
                    <div className="document-name">
                      <Link to={documentLink}>{document.name || 'Untitled Document'}</Link>
                    </div>
                    <span className={`document-status ${status.className}`}>
                      {status.text}
                    </span>
                  </div>
                  
                  <div className="document-card-content">
                    <div className="document-info-row">
                      <div className="document-info-label">Signature Progress</div>
                      <div className="document-info-value">
                        <SignatureProgress document={document} />
                      </div>
                    </div>
                    
                    <div className="document-info-row">
                      <div className="document-info-label">Created</div>
                      <div className="document-info-value date-value">
                        {formatDate(document.created_at)}
                      </div>
                    </div>
                    
                    <div className="document-info-row">
                      <div className="document-info-label">
                        {document.status === 'signed' ? 'Signed' : 'Updated'}
                      </div>
                      <div className="document-info-value date-value">
                        {document.status === 'signed' && document.signed_at ? 
                          formatDate(document.signed_at) : 
                          formatDate(document.updated_at)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="document-card-footer">
                    <Link to={documentLink}>
                      <Button variant="tertiary" size="small">
                        {document.status === 'draft' ? 'Edit' : 'View'}
                      </Button>
                    </Link>
                    <Button 
                      variant="danger" 
                      size="small"
                      onClick={() => handleDeleteClick(document.id)}
                      loading={deletingDocuments[document.id]}
                      disabled={deletingDocuments[document.id]}
                      aria-label="Delete document"
                    >
                      Delete
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
